import * as React from "react";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

export default function ProductDetailsSkeleton() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Responsive image sizes
  const mainImgSize = isXs ? 180 : isSm ? 240 : 320;
  const thumbSize = isXs ? 36 : isSm ? 44 : 56;

  return (
    <Grid
      container
      spacing={isXs ? 2 : 4}
      sx={{
        maxWidth: 1200,
        mx: "auto",
        py: isXs ? 2 : 4,
        px: isXs ? 1 : 3,
      }}
    >
      {/* Product Image Section */}
      <Grid item xs={12} md={5}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isXs ? 2 : 3,
          }}
        >
          {/* Main Image */}
          <Skeleton
            variant="rectangular"
            width={mainImgSize}
            height={mainImgSize}
            sx={{ borderRadius: 3, mb: 1, width: "100%", maxWidth: mainImgSize }}
          />
          {/* Image Zoom/Fullscreen Button */}
          <Skeleton
            variant="circular"
            width={isXs ? 28 : 36}
            height={isXs ? 28 : 36}
            sx={{ position: "absolute", top: 16, right: 16, zIndex: 1, display: { xs: "none", md: "block" } }}
          />
          {/* Thumbnails */}
          <Stack direction="row" spacing={1} sx={{ mt: 1, width: "100%", justifyContent: "center" }}>
            {[1, 2, 3, 4, 5].map((_, i) => (
              <Skeleton key={i} variant="circular" width={thumbSize} height={thumbSize} />
            ))}
          </Stack>
          {/* Image Caption */}
          <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
        </Box>
      </Grid>
      {/* Product Details Section */}
      <Grid item xs={12} md={7}>
        <Stack spacing={isXs ? 1.5 : 2.5}>
          {/* Title */}
          <Skeleton variant="text" width="80%" height={isXs ? 28 : 40} sx={{ fontSize: isXs ? "1.3rem" : "2rem" }} />
          {/* Brand */}
          <Skeleton variant="text" width="40%" height={isXs ? 18 : 24} />
          {/* Price */}
          <Skeleton variant="text" width="30%" height={isXs ? 22 : 32} sx={{ fontSize: isXs ? "1.1rem" : "1.5rem" }} />
          {/* Discount/Offer */}
          <Skeleton variant="rectangular" width={isXs ? 60 : 90} height={isXs ? 18 : 24} sx={{ borderRadius: 2 }} />
          {/* Rating & Reviews */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="rectangular" width={isXs ? 70 : 120} height={isXs ? 18 : 28} sx={{ borderRadius: 8 }} />
            <Skeleton variant="text" width={isXs ? 40 : 60} height={isXs ? 16 : 22} />
          </Stack>
          {/* Availability */}
          <Skeleton variant="text" width="30%" height={isXs ? 16 : 22} />
          {/* Short Description */}
          <Skeleton variant="text" width="95%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="80%" />
          {/* Features List */}
          <Stack spacing={1}>
            {[1, 2, 3].map((_, i) => (
              <Skeleton key={i} variant="text" width={`${80 - i * 10}%`} height={isXs ? 14 : 18} />
            ))}
          </Stack>
          {/* Color/Size Options */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="text" width={isXs ? 40 : 60} height={isXs ? 16 : 22} />
            {[1, 2, 3].map((_, i) => (
              <Skeleton key={i} variant="circular" width={isXs ? 20 : 28} height={isXs ? 20 : 28} />
            ))}
          </Stack>
          {/* Quantity Selector */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="rectangular" width={isXs ? 60 : 80} height={isXs ? 28 : 36} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width={isXs ? 30 : 40} height={isXs ? 16 : 22} />
          </Stack>
          {/* Add to Cart & Buy Now Buttons */}
          <Stack direction={isXs ? "column" : "row"} spacing={2} sx={{ mt: 2 }}>
            <Skeleton variant="rounded" width={isXs ? "100%" : 180} height={isXs ? 38 : 48} />
            <Skeleton variant="rounded" width={isXs ? "100%" : 140} height={isXs ? 38 : 48} />
          </Stack>
          {/* Wishlist/Compare/Share */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Skeleton variant="circular" width={isXs ? 28 : 36} height={isXs ? 28 : 36} />
            <Skeleton variant="circular" width={isXs ? 28 : 36} height={isXs ? 28 : 36} />
            <Skeleton variant="circular" width={isXs ? 28 : 36} height={isXs ? 28 : 36} />
          </Stack>
        </Stack>
      </Grid>
      {/* Product Tabs/Additional Info */}
      <Grid item xs={12}>
        <Stack spacing={2} sx={{ mt: isXs ? 2 : 4 }}>
          {/* Tabs */}
          <Stack direction="row" spacing={2}>
            {[1, 2, 3].map((_, i) => (
              <Skeleton key={i} variant="rounded" width={isXs ? 70 : 120} height={isXs ? 28 : 36} />
            ))}
          </Stack>
          {/* Tab Content */}
          <Skeleton variant="text" width="90%" height={isXs ? 16 : 22} />
          <Skeleton variant="text" width="95%" height={isXs ? 16 : 22} />
          <Skeleton variant="text" width="80%" height={isXs ? 16 : 22} />
        </Stack>
      </Grid>
    </Grid>
  );
}
